import { z } from 'zod';
import { AdminSubscriptionActivationOperation, AdminSubscriptionBillingCycle } from './admin';
import { PlanCode } from './plan';

const AdminBillingIdentifier = z.string().trim().min(1).max(256);
const AdminBillingTimestamp = z.string().datetime({ offset: true });
const AdminBillingMoney = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const AdminBillingCurrency = z.string().trim().min(3).max(12);

export const AdminBillingAmountKind = z.enum(['free', 'paid']);
export type AdminBillingAmountKind = z.infer<typeof AdminBillingAmountKind>;

export const AdminBillingInvoiceStatus = z.enum([
  'draft',
  'open',
  'paid',
  'void',
  'uncollectible',
  'refunded',
  'partial_refund',
]);
export type AdminBillingInvoiceStatus = z.infer<typeof AdminBillingInvoiceStatus>;

export const AdminBillingPaymentStatus = z.enum([
  'pending',
  'succeeded',
  'failed',
  'refunded',
  'partial_refund',
]);
export type AdminBillingPaymentStatus = z.infer<typeof AdminBillingPaymentStatus>;

export const AdminBillingPaymentStatusFilter = z.enum([
  'none',
  'pending',
  'succeeded',
  'failed',
  'refunded',
  'partial_refund',
]);
export type AdminBillingPaymentStatusFilter = z.infer<typeof AdminBillingPaymentStatusFilter>;

export const AdminBillingPaymentMethod = z.enum([
  'card',
  'alipay',
  'wechat_pay',
  'apple_pay',
  'google_pay',
  'other',
]);
export type AdminBillingPaymentMethod = z.infer<typeof AdminBillingPaymentMethod>;

export const AdminBillingPaymentSource = z.enum(['creem', 'admin_grant', 'admin_offline_payment']);
export type AdminBillingPaymentSource = z.infer<typeof AdminBillingPaymentSource>;

export const AdminBillingPaymentSourceFilter = z.enum([
  'none',
  'creem',
  'admin_grant',
  'admin_offline_payment',
]);
export type AdminBillingPaymentSourceFilter = z.infer<typeof AdminBillingPaymentSourceFilter>;

export const AdminBillingLedgerQuery = z
  .object({
    tenant_id: z.string().trim().min(1).max(128).optional(),
    amount_kind: AdminBillingAmountKind.optional(),
    invoice_status: AdminBillingInvoiceStatus.optional(),
    payment_status: AdminBillingPaymentStatusFilter.optional(),
    payment_source: AdminBillingPaymentSourceFilter.optional(),
    created_from: AdminBillingTimestamp.optional(),
    created_to: AdminBillingTimestamp.optional(),
    cursor: z.string().trim().min(1).max(400).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict()
  .refine(
    (value) =>
      !value.created_from ||
      !value.created_to ||
      Date.parse(value.created_from) < Date.parse(value.created_to),
    {
      path: ['created_to'],
      message: '`created_from` must be earlier than `created_to`',
    },
  );
export type AdminBillingLedgerQuery = z.infer<typeof AdminBillingLedgerQuery>;

export const AdminBillingLedgerInvoice = z
  .object({
    id: AdminBillingIdentifier,
    number: z.string().trim().min(1).max(256),
    tenant_id: AdminBillingIdentifier,
    tenant_name: z.string().min(1),
    subscription_id: AdminBillingIdentifier.nullable(),
    amount_cents: AdminBillingMoney,
    tax_cents: AdminBillingMoney,
    currency: AdminBillingCurrency,
    status: AdminBillingInvoiceStatus,
    due_date: AdminBillingTimestamp.nullable(),
    paid_at: AdminBillingTimestamp.nullable(),
    created_at: AdminBillingTimestamp,
    updated_at: AdminBillingTimestamp,
  })
  .strict();
export type AdminBillingLedgerInvoice = z.infer<typeof AdminBillingLedgerInvoice>;

export const AdminBillingLatestPayment = z
  .object({
    id: AdminBillingIdentifier,
    amount_cents: AdminBillingMoney,
    currency: AdminBillingCurrency,
    status: AdminBillingPaymentStatus,
    method: AdminBillingPaymentMethod,
    source: AdminBillingPaymentSource,
    paid_at: AdminBillingTimestamp.nullable(),
    created_at: AdminBillingTimestamp,
    updated_at: AdminBillingTimestamp,
  })
  .strict();
export type AdminBillingLatestPayment = z.infer<typeof AdminBillingLatestPayment>;

export const AdminBillingSubscriptionActivationContext = z
  .object({
    kind: z.literal('subscription_activation'),
    activation_id: AdminBillingIdentifier,
    operation: AdminSubscriptionActivationOperation,
    previous_plan_code: PlanCode,
    target_plan_code: PlanCode,
    billing_cycle: AdminSubscriptionBillingCycle.nullable(),
    admin_id: AdminBillingIdentifier,
    admin_display_name: z.string().min(1).nullable(),
  })
  .strict();
export type AdminBillingSubscriptionActivationContext = z.infer<
  typeof AdminBillingSubscriptionActivationContext
>;

export const AdminBillingLedgerItem = z
  .object({
    invoice: AdminBillingLedgerInvoice,
    amount_kind: AdminBillingAmountKind,
    payment_count: z.number().int().nonnegative(),
    latest_payment: AdminBillingLatestPayment.nullable(),
    business_context: AdminBillingSubscriptionActivationContext.nullable(),
  })
  .strict();
export type AdminBillingLedgerItem = z.infer<typeof AdminBillingLedgerItem>;

export const AdminBillingLedgerListResponse = z
  .object({
    items: z.array(AdminBillingLedgerItem),
    next_cursor: z.string().min(1).nullable(),
    limit: z.number().int().min(1).max(100),
  })
  .strict();
export type AdminBillingLedgerListResponse = z.infer<typeof AdminBillingLedgerListResponse>;

export const AdminBillingLedgerDetailInvoice = AdminBillingLedgerInvoice.extend({
  creem_invoice_id: z.string().min(1).nullable(),
}).strict();
export type AdminBillingLedgerDetailInvoice = z.infer<typeof AdminBillingLedgerDetailInvoice>;

export const AdminBillingPaymentAttempt = AdminBillingLatestPayment.extend({
  creem_payment_id: z.string().min(1).nullable(),
  failure_code: z.string().nullable(),
  failure_message: z.string().nullable(),
}).strict();
export type AdminBillingPaymentAttempt = z.infer<typeof AdminBillingPaymentAttempt>;

export const AdminBillingSubscription = z
  .object({
    id: AdminBillingIdentifier,
    plan_code: PlanCode,
  })
  .strict();
export type AdminBillingSubscription = z.infer<typeof AdminBillingSubscription>;

export const AdminBillingSubscriptionActivation = z
  .object({
    id: AdminBillingIdentifier,
    admin_id: AdminBillingIdentifier,
    admin_display_name: z.string().min(1).nullable(),
    tenant_id: AdminBillingIdentifier,
    idempotency_key: AdminBillingIdentifier,
    operation: AdminSubscriptionActivationOperation,
    previous_plan_code: PlanCode,
    target_plan_code: PlanCode,
    billing_cycle: AdminSubscriptionBillingCycle.nullable(),
    amount_cents: AdminBillingMoney,
    currency: AdminBillingCurrency,
    payment_source: AdminBillingPaymentSource,
    period_start: AdminBillingTimestamp.nullable(),
    period_end: AdminBillingTimestamp.nullable(),
    previous_subscription_id: AdminBillingIdentifier.nullable(),
    subscription_id: AdminBillingIdentifier.nullable(),
    invoice_id: AdminBillingIdentifier,
    payment_id: AdminBillingIdentifier,
    reason: z.string().min(1).max(500),
    created_at: AdminBillingTimestamp,
  })
  .strict();
export type AdminBillingSubscriptionActivation = z.infer<typeof AdminBillingSubscriptionActivation>;

export const AdminBillingLedgerDetailResponse = AdminBillingLedgerItem.extend({
  invoice: AdminBillingLedgerDetailInvoice,
  payments: z.array(AdminBillingPaymentAttempt),
  subscription: AdminBillingSubscription.nullable(),
  subscription_activation: AdminBillingSubscriptionActivation.nullable(),
}).strict();
export type AdminBillingLedgerDetailResponse = z.infer<typeof AdminBillingLedgerDetailResponse>;
